<template>
	<div class="editor-control-bar sgnl-flex sgnl-align-center userflow-editor-control-bar sgnl-gap-12">
		<div class="sgnl-flex-shrink-0 userflow-run-button-editor">
			<LimitableActionButton
				@valid-click="postAsJob"
				variant="flat"
				color="primary"
				:loading="actionButtonLoading"
				tooltipText="Compile and Run"
				data-test="build-and-run-button"
				:limits-to-check="[UserLimitsE.ConcurrentTaskCount, UserLimitsE.DynamicInstructionCount]"
			>
				<template #btnContent>
					<PlayFilledAlt16 />
					<span>Run</span>
				</template>
			</LimitableActionButton>
		</div>
		<div
			class="sgnl-flex sgnl-flex-wrap sgnl-align-center sgnl-justify-start sgnl-justify-sm-center sgnl-flex-grow-1 sgnl-gap-8-24"
		>
			<!-- Build section -->
			<div
				v-if="buildStatus !== undefined"
				class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
			>
				<span
					class="control-bar-label sgnl-text-color-secondary sgnl-paragraph-s-medium"
					data-test="build-id-label"
					>Build:</span
				>
				<div
					v-if="buildID !== undefined || activeBuildStatus"
					class="control-bar-id-slot"
				>
					<BuildIDDisplay
						v-if="buildID !== undefined"
						:buildID="buildID"
						:showClipboardButton="true"
					/>
					<SkeletonBlock
						v-else
						width="140px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
				</div>
				<div class="control-bar-status-active-slot">
					<span
						class="sgnl-paragraph-s-regular control-bar-status-text"
						:class="`sgnl-text-color-${buildStatusColor}`"
						data-test="project-status-value"
						>{{ buildStatusText }}</span
					>
					<div class="control-bar-status-icon-slot">
						<v-tooltip
							v-if="buildAccessState?.error"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<WarningAlt16
									v-bind="props"
									class="control-bar-warning-icon"
								/>
							</template>
							<span>{{ buildAccessState.message }}</span>
						</v-tooltip>
						<v-progress-circular
							v-else-if="activeBuildStatus"
							indeterminate
							size="16"
							width="2"
							:color="buildStatusColor"
						/>
					</div>
					<v-btn
						v-if="buildIsActive"
						variant="outlined"
						color="error"
						size="small"
						:disabled="!buildID"
						:loading="cancel.isCancellingBuild(buildID)"
						@click="sendCancelActiveBuild"
					>
						<template v-if="!$vuetify.display.mobile">Cancel</template>
						<StopOutline16 v-else />
					</v-btn>
				</div>
			</div>
			<!-- Task section (visible whenever the run pipeline is active) -->
			<div
				v-if="taskStatus !== undefined"
				class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
			>
				<span
					class="control-bar-label sgnl-text-color-secondary sgnl-paragraph-s-medium"
					data-test="task-id-label"
					>Task:</span
				>
				<div
					v-if="taskID !== undefined || activeTaskStatus"
					class="control-bar-id-slot"
				>
					<TaskIDDisplay
						v-if="taskID !== undefined"
						:taskID="taskID"
						:link="{ name: 'TaskDetail', params: { id: taskID } }"
						:showClipboardButton="true"
					/>
					<SkeletonBlock
						v-else
						width="140px"
						height="var(--sgnl-unit-24)"
						radius="var(--sgnl-border-radius-6)"
					/>
				</div>
				<div class="control-bar-status-active-slot">
					<span
						class="sgnl-paragraph-s-regular control-bar-status-text"
						:class="`sgnl-text-color-${taskStatusColor}`"
						data-test="project-status-value"
						>{{ taskStatusText }}</span
					>
					<div class="control-bar-status-icon-slot">
						<v-tooltip
							v-if="taskAccessState?.error"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<WarningAlt16
									v-bind="props"
									class="control-bar-warning-icon"
								/>
							</template>
							<span>{{ taskAccessState.message }}</span>
						</v-tooltip>
						<v-progress-circular
							v-else-if="activeTaskStatus"
							indeterminate
							size="16"
							width="2"
							:color="taskStatusColor"
						/>
					</div>
					<v-btn
						v-if="taskIsActive"
						variant="outlined"
						color="error"
						size="small"
						:disabled="!taskID"
						:loading="cancel.isCancellingTask(taskID)"
						@click="sendCancelActiveTask"
					>
						<template v-if="!$vuetify.display.mobile">Cancel</template>
						<StopOutline16 v-else />
					</v-btn>
				</div>
			</div>
			<!-- Deduplicated fetch error (build + task same message) -->
			<div
				v-if="dedupedFetchError"
				class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
			>
				<ResourceErrorDisplay
					:view="buildErrorView"
					:showViewTask="true"
					:taskID="taskID"
					@retry="retryBoth"
				/>
			</div>
			<template v-else>
				<div
					v-if="buildErrorView"
					class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
				>
					<ResourceErrorDisplay
						:view="buildErrorView"
						@retry="sendRetryBuildOutput"
					/>
				</div>
				<div
					v-if="buildFetchRetry"
					class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
				>
					<ResourceErrorDisplay
						:view="buildFetchRetry"
						@retry="sendRetryBuildOutput"
					/>
				</div>
				<div
					v-if="taskErrorView"
					class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
				>
					<ResourceErrorDisplay
						:view="taskErrorView"
						:showViewTask="true"
						:taskID="taskID"
						@retry="sendRetryTaskOutput"
					/>
				</div>
				<div
					v-if="taskFetchRetry"
					class="sgnl-flex sgnl-align-center sgnl-flex-nowrap sgnl-gap-8"
				>
					<ResourceErrorDisplay
						:view="taskFetchRetry"
						:showViewTask="true"
						:taskID="taskID"
						@retry="sendRetryTaskOutput"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, inject, PropType, toRef } from "vue";
import { CancelFeedbackKey, CANCEL_FEEDBACK_NOOP } from "@/composables/useCancelWithFeedback";
import { useStuckResourceMessage } from "@/composables/useStuckResourceMessage";
import WarningAlt16 from "@carbon/icons-vue/es/warning--alt/16";
import PlayFilledAlt16 from "@carbon/icons-vue/es/play--filled--alt/16";
import StopOutline16 from "@carbon/icons-vue/es/stop--outline/16";
// Components
import TaskIDDisplay from "@/components/TaskIDDisplay.vue";
import BuildIDDisplay from "@/components/BuildIDDisplay.vue";
import ResourceErrorDisplay from "@/components/Common/ResourceErrorDisplay.vue";
import LimitableActionButton from "@/components/Common/LimitableActionButton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";

// Types
import { isTaskActive, isTaskFail, Task, TaskStatus, TaskStatusE } from "@/types/api/tasks";
import { RemoteAccessState } from "@/types/general";
import { UserLimitsE } from "@/js/tierconfig";

// Utilities
import * as util from "@/js/utilities";
import { Build, BuildStatus, isBuildActive, isBuildFail } from "@/types/api/builds";

export default defineComponent({
	name: "EditorControlBar",
	components: {
		TaskIDDisplay,
		BuildIDDisplay,
		ResourceErrorDisplay,
		LimitableActionButton,
		SkeletonBlock,
		WarningAlt16,
		PlayFilledAlt16,
		StopOutline16,
	},
	emits: [
		"create-task-request",
		"cancel-active-task-request",
		"cancel-active-build-request",
		"retry-task-output",
		"retry-build-output",
	],
	props: {
		taskStatus: { type: String as PropType<TaskStatus> },
		taskID: { type: String },
		taskAccessState: { type: Object as PropType<RemoteAccessState> },
		activeTask: { type: Object as PropType<undefined | Partial<Task>> },
		buildID: { type: String },
		buildStatus: { type: String as PropType<BuildStatus> },
		activeBuild: { type: Object as PropType<undefined | Partial<Build>> },
		buildAccessState: { type: Object as PropType<RemoteAccessState> },
	},
	setup(props) {
		const cancel = inject(CancelFeedbackKey, null);
		if (!cancel && !import.meta.env.PROD) {
			console.warn("[EditorControlBar] No CancelFeedbackKey provider found in ancestor chain.");
		}
		const taskStuckMessage = useStuckResourceMessage(toRef(props, "activeTask"), "task");
		const buildStuckMessage = useStuckResourceMessage(toRef(props, "activeBuild"), "build");
		return {
			cancel: cancel ?? CANCEL_FEEDBACK_NOOP,
			isTaskActive,
			TaskStatusE,
			UserLimitsE,
			taskStuckMessage,
			buildStuckMessage,
		};
	},
	computed: {
		taskStatusMessageOnError: function (): string | null {
			if (this.activeTask?.Status && !isTaskFail(this.activeTask.Status)) {
				return null;
			}
			const statusTransition =
				this.activeTask?.StatusTransitions &&
				this.activeTask.StatusTransitions.filter((st) => st.Status == this.activeTask?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}
			return statusTransition?.Message ?? null;
		},
		buildStatusMessageOnError: function (): string | null {
			if (this.activeBuild?.Status && !isBuildFail(this.activeBuild.Status)) {
				return null;
			}
			const statusTransition =
				this.activeBuild?.StatusTransitions &&
				this.activeBuild.StatusTransitions.filter((st) => st.Status == this.activeBuild?.Status)[0];

			// Kea-cloud errors are formatted as `ErrXXX: Error message.`, where `ErrXXX` is an enum-like identifier.
			// This code checks if the message contains a colon, splits it, and returns the error message without the prefix.
			if (statusTransition && statusTransition.Message?.includes(":")) {
				const messageParts = statusTransition.Message.split(":");
				return messageParts.slice(1).join(":").trim() || null;
			}
			return statusTransition?.Message ?? null;
		},
		buildStatusColor(): string {
			return util.buildStatusColor(this.buildStatus);
		},
		taskStatusColor(): string {
			return util.taskStatusColor(this.taskStatus);
		},
		activeBuildStatus(): boolean {
			return this.buildStatus ? isBuildActive(this.buildStatus) : false;
		},
		activeTaskStatus(): boolean {
			return this.taskStatus ? isTaskActive(this.taskStatus) : false;
		},
		buildStatusText(): string {
			// function to trim long status names in mobile
			if (!this.$vuetify.display.mobile) {
				return this.buildStatus ?? "";
			}

			switch (this.buildStatus) {
				case "Tier Limit Reached":
					return "Tier Limited";
				default:
					return this.buildStatus ?? "";
			}
		},
		taskStatusText(): string {
			// function to trim long status names in mobile
			if (!this.$vuetify.display.mobile) {
				return this.taskStatus ?? "";
			}

			switch (this.taskStatus) {
				case "Task Not Found":
					return "Not Found";
				case "Tier Limit Reached":
					return "Tier Limited";
				case "Task Rejected":
					return "Rejected";
				case "Upgrade Your Account":
					return "Upgrade Account";
				default:
					return this.taskStatus ?? "";
			}
		},
		buildIsActive(): boolean | undefined {
			return this.buildStatus && isBuildActive(this.buildStatus);
		},
		taskIsActive(): boolean | undefined {
			return this.taskStatus && isTaskActive(this.taskStatus);
		},
		actionButtonLoading(): boolean | undefined {
			return this.buildIsActive || this.taskIsActive;
		},
		buildErrorView(): util.ResourceErrorView {
			return util.resolveResourceError(
				this.buildStatusMessageOnError,
				this.buildAccessState,
				this.buildStuckMessage,
			);
		},
		taskErrorView(): util.ResourceErrorView {
			return util.resolveResourceError(
				this.taskStatusMessageOnError,
				this.taskAccessState,
				this.taskStuckMessage,
			);
		},
		buildFetchRetry(): util.ResourceErrorView {
			return util.resolveFetchRetry(this.buildStatusMessageOnError, this.buildAccessState);
		},
		taskFetchRetry(): util.ResourceErrorView {
			return util.resolveFetchRetry(this.taskStatusMessageOnError, this.taskAccessState);
		},
		dedupedFetchError(): boolean {
			return util.isDedupedFetchError(this.buildErrorView, this.taskErrorView);
		},
	},
	methods: {
		postAsJob() {
			// @ts-ignore
			this.$posthog?.capture("run_source_task_button_clicked");
			this.$emit("create-task-request");
		},
		sendCancelActiveTask() {
			this.$emit("cancel-active-task-request");
		},
		sendCancelActiveBuild() {
			this.$emit("cancel-active-build-request");
		},
		sendRetryTaskOutput() {
			this.$emit("retry-task-output");
		},
		sendRetryBuildOutput() {
			this.$emit("retry-build-output");
		},
		retryBoth() {
			this.$emit("retry-build-output");
			this.$emit("retry-task-output");
		},
	},
});
</script>

<style scoped>
.editor-control-bar {
	border-top: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	flex-shrink: 0;
	padding: var(--sgnl-space-8) var(--sgnl-space-12);
	font-size: var(--sgnl-font-size-paragraph-s);
}

.control-bar-label {
	white-space: nowrap;
}

.control-bar-id-slot {
	display: flex;
	align-items: center;
	min-width: 140px;
	flex-shrink: 0;
}

.control-bar-id-slot :deep(.copy-id-chip) {
	min-width: 100%;
}

.control-bar-status-active-slot {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
	min-width: 176px;
	flex-shrink: 0;
	white-space: nowrap;
}

.control-bar-status-text {
	display: inline-block;
	min-width: 80px;
}

.control-bar-status-icon-slot {
	width: var(--sgnl-icon-16);
	height: var(--sgnl-icon-16);
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}

.control-bar-warning-icon {
	color: var(--sgnl-alert-warning-icon);
	flex-shrink: 0;
}

@media (max-width: 599px) {
	.editor-control-bar {
		flex-direction: column;
		align-items: stretch;
	}

	.editor-control-bar .sgnl-flex-shrink-0 {
		width: 100%;
	}

	.control-bar-id-slot,
	.control-bar-status-active-slot {
		min-width: 0;
	}
}
</style>
